// Settings — Locations tab. Settings-02.
//
// Lists the broker's office locations and lets admins create / edit /
// deactivate / restore rows. Account → primary-location wiring lands
// in Accounts-Teams-01.

import { useMemo, useState } from "react";

import {
  Button, Table, TBody, THead, Tr, Th, Td,
} from "@/design-system";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { LocationModal } from "./LocationModal";
import {
  useArchiveBrokerLocationMutation,
  useBrokerLocationsQuery,
  useCreateBrokerLocationMutation,
  useRestoreBrokerLocationMutation,
  useUpdateBrokerLocationMutation,
} from "./hooks";
import type {
  BrokerLocation,
  BrokerLocationCreateInput,
} from "./types";


export function LocationsTab() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const locationsQuery = useBrokerLocationsQuery({ includeArchived });
  const createMutation = useCreateBrokerLocationMutation();
  const updateMutation = useUpdateBrokerLocationMutation();
  const archiveMutation = useArchiveBrokerLocationMutation();
  const restoreMutation = useRestoreBrokerLocationMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BrokerLocation | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<BrokerLocation | null>(null);

  const rows = useMemo(
    () => locationsQuery.data?.rows ?? [],
    [locationsQuery.data],
  );

  const onCreate = () => {
    setEditing(null);
    setModalError(null);
    setModalOpen(true);
  };

  const onEdit = (row: BrokerLocation) => {
    setEditing(row);
    setModalError(null);
    setModalOpen(true);
  };

  const onSubmit = (input: BrokerLocationCreateInput) => {
    setModalError(null);
    if (editing) {
      updateMutation.mutate(
        { location_id: editing.location_id, values: input },
        {
          onSuccess: () => setModalOpen(false),
          onError: (err) =>
            setModalError(describeError(err, "Couldn't save location.")),
        },
      );
    } else {
      createMutation.mutate(input, {
        onSuccess: () => setModalOpen(false),
        onError: (err) =>
          setModalError(describeError(err, "Couldn't add location.")),
      });
    }
  };

  const onConfirmArchive = () => {
    if (!archiving) return;
    archiveMutation.mutate(archiving.location_id, {
      onSettled: () => setArchiving(null),
    });
  };

  const onRestore = (row: BrokerLocation) => {
    restoreMutation.mutate(row.location_id);
  };

  return (
    <div data-testid="settings-locations-tab" className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-md font-semibold text-text-primary">
            Office locations
          </h2>
          <p className="mt-1 max-w-2xl text-xs text-text-secondary">
            Add the broker offices you operate from. Inactive locations
            stay available for historical reporting but should not be
            assigned to new Accounts.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-xs text-text-secondary">
            <input
              type="checkbox"
              data-testid="settings-locations-include-archived"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show inactive
          </label>
          <Button
            variant="primary"
            data-testid="settings-locations-add-button"
            onClick={onCreate}
          >
            + Add location
          </Button>
        </div>
      </header>

      {locationsQuery.isLoading ? (
        <DashboardLoadingState variant="table" rows={3} />
      ) : locationsQuery.isError ? (
        <DashboardErrorState
          title="Couldn't load locations"
          description="Refresh to retry."
          retry={() => locationsQuery.refetch()}
        />
      ) : rows.length === 0 ? (
        <DashboardEmptyState
          title="No locations yet"
          description="Add office locations so Accounts and dashboards can be filtered by office."
        />
      ) : (
        <Table
          wrapperClassName="rounded-lg border border-border-subtle"
          layout="fixed"
          className="border-collapse"
          data-testid="settings-locations-table"
        >
          <colgroup>
            <col style={{ width: "28%" }} />
            <col style={{ width: "44%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "16%" }} />
          </colgroup>
          <THead className="bg-canvas/40 text-xs uppercase tracking-wide text-text-tertiary">
            <Tr>
              <Th>Name</Th>
              <Th>Address</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <LocationRow
                key={row.location_id}
                row={row}
                onEdit={() => onEdit(row)}
                onArchive={() => setArchiving(row)}
                onRestore={() => onRestore(row)}
                pending={
                  archiveMutation.isPending
                  || restoreMutation.isPending
                }
              />
            ))}
          </TBody>
        </Table>
      )}

      <LocationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        location={editing}
        saving={createMutation.isPending || updateMutation.isPending}
        error={modalError}
        onSubmit={onSubmit}
      />

      <ConfirmDialog
        open={archiving !== null}
        title="Deactivate location?"
        body={
          archiving
            ? `${archiving.name} will move to inactive. Inactive locations stay available for historical reporting but should not be assigned to new Accounts.`
            : ""
        }
        confirmLabel="Deactivate"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={onConfirmArchive}
        onCancel={() => setArchiving(null)}
        testId="settings-locations-archive-confirm"
      />
    </div>
  );
}


function LocationRow({
  row, onEdit, onArchive, onRestore, pending,
}: {
  row: BrokerLocation;
  onEdit: () => void;
  onArchive: () => void;
  onRestore: () => void;
  pending: boolean;
}) {
  const archived = row.archived_at !== null;
  const cityState = [row.city, row.state_code].filter(Boolean).join(", ");
  const zipBlock = [cityState, row.postal_code].filter(Boolean).join(" ");
  const address = [row.address_line1, zipBlock].filter(Boolean).join(" · ");
  return (
    <Tr
      data-testid={`settings-location-row-${row.location_id}`}
      data-status={row.status}
      className="border-t border-border-subtle"
    >
      <Td className="text-sm font-medium text-text-primary">
        {row.name}
      </Td>
      <Td className="text-sm text-text-secondary">
        {address || (
          <span className="italic text-text-tertiary">No address</span>
        )}
      </Td>
      <Td>
        <StatusBadge status={row.status} />
      </Td>
      <Td className="text-right text-xs">
        <div className="inline-flex items-center gap-2">
          <button
            type="button"
            data-testid={`settings-location-edit-${row.location_id}`}
            onClick={onEdit}
            className="font-medium text-teal hover:underline"
          >
            Edit
          </button>
          {archived ? (
            <button
              type="button"
              data-testid={`settings-location-restore-${row.location_id}`}
              onClick={onRestore}
              disabled={pending}
              className="font-medium text-teal hover:underline disabled:opacity-50"
            >
              Restore
            </button>
          ) : (
            <button
              type="button"
              data-testid={`settings-location-archive-${row.location_id}`}
              onClick={onArchive}
              disabled={pending}
              className="font-medium text-ember hover:underline disabled:opacity-50"
            >
              Deactivate
            </button>
          )}
        </div>
      </Td>
    </Tr>
  );
}


function StatusBadge({ status }: { status: BrokerLocation["status"] }) {
  if (status === "active") {
    return (
      <span
        data-testid="settings-location-status-active"
        className="inline-flex items-center rounded-full bg-sage-tint px-2 py-0.5 text-[11px] font-medium text-teal"
      >
        Active
      </span>
    );
  }
  return (
    <span
      data-testid="settings-location-status-inactive"
      className="inline-flex items-center rounded-full bg-recessed px-2 py-0.5 text-[11px] font-medium text-text-tertiary"
    >
      Inactive
    </span>
  );
}


function describeError(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  return fallback;
}
